'use client';

import { type Product } from '@/domain/products/types';
import { SuccessToast } from '@/features/cart/components';
import { addToCart, useCartStore } from '@/features/cart/store/cart';
import { buildSolutionUrl } from '@/utils/string';
import { Typography } from '@andritzot/metris-web-ui/data-display/typography';
import { Button } from '@andritzot/metris-web-ui/inputs/button';
import { Box } from '@andritzot/metris-web-ui/layout/content/Box';
import { usePiwikPro } from '@piwikpro/next-piwik-pro';
import { useCallback } from 'react';
import { toast } from 'react-toastify';

import ProductDetail from '../../ProductDetailDialog/ProductDetail';

interface ProductDetailsPageProps {
  detailedProduct: Product;
  isInternal: boolean;
}

const ProductDetailsPage = ({ detailedProduct, isInternal }: ProductDetailsPageProps) => {
  const cartProducts = useCartStore((s) => s.cartProducts);
  const { eCommerce } = usePiwikPro();

  const addedToCart = cartProducts.some((p) => p.idProduct === detailedProduct.idProduct);

  const handleAddToCart = useCallback(() => {
    if (!detailedProduct) return;
    eCommerce.ecommerceAddToCart([
      {
        sku: `${detailedProduct.idProduct}`,
        name: detailedProduct.product,
        category: [detailedProduct.group.productGroup],
        price: 0,
        quantity: 1,
      },
    ]);
    addToCart({
      group: detailedProduct.group.productGroup,
      idProduct: detailedProduct.idProduct,
      product: detailedProduct.product,
      image2: detailedProduct.image2,
      documentation: detailedProduct.documentation,
    });
    toast.success(<SuccessToast title={detailedProduct.product} />, { autoClose: 5000 });
  }, [detailedProduct, eCommerce]);

  const handleCopyURL = useCallback(async () => {
    await navigator.clipboard.writeText(
      `${globalThis.location.origin}${buildSolutionUrl(detailedProduct)}`,
    );
    toast.success('Copied to clipboard!');
  }, [detailedProduct]);

  const headerActions = (
    <Box sx={{ display: 'flex' }}>
      <Box sx={{ display: 'flex' }}>
        <Button size="small" variant="outlined" sx={{ textTransform: 'uppercase', mr: 2 }} onClick={handleCopyURL}>
          Copy URL
        </Button>
        <Button
          size="small"
          variant="outlined"
          disabled={addedToCart}
          sx={{ textTransform: 'uppercase', mr: 2 }}
          onClick={handleAddToCart}
        >
          {addedToCart ? 'ADDED' : 'ADD TO CART'}
        </Button>
      </Box>
    </Box>
  );

  const headerTitle = (
    <Typography component="h4" sx={{ textTransform: 'capitalize', m: 0, fontSize: 24, color: 'text.dark' }}>
      {detailedProduct?.product}
    </Typography>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p:4}}>
        {headerTitle}
        {headerActions}
      </Box>
      <ProductDetail detailedProduct={detailedProduct} isInternal={isInternal} />
    </Box>
  );
};

export default ProductDetailsPage;
